"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { FiInfo } from "react-icons/fi";
import { BiWorld } from "react-icons/bi";
import { GiBrain } from "react-icons/gi";
import {
  HiOutlineBookOpen,
  HiOutlineMicrophone,
  HiOutlinePencilAlt,
  HiOutlineVolumeUp,
} from "react-icons/hi";
import { MdOutlineChat } from "react-icons/md";
import { IoGameControllerOutline } from "react-icons/io5";
import { TbCategory2 } from "react-icons/tb";
import QuestionBlock from "./questionBlock";
import StatusPopUp from "../../popup/statusPopUp";
import { buildGameSuccessStatus } from "../shared/gameStatusMessages";
import { areQuizQuestionsSaveReady } from "../shared/mediaValidation";
import ConfirmPopUp from "../../popup/confirmPopUp";
import multipleChoiceAPI from "../../../services/multipleChoice/multipleChoice";
import uploadAPI from "../../../services/upload/uploadAPI";
import mainAPI from "../../../services/main/mainAPI";
import { collectPersistedIdsFromApiRows } from "../shared/serverPersistedIds";
import { GamePreviewModal } from "../shared/GamePreviewModal";
import {
  commitSavedQuizQuestionsGameSnapshot,
  createQuizQuestionsGameSnapshot,
  getCanGamePreview,
  getShouldConfirmLeaveGameEditor,
  patchSavedQuizQuestionsSnapshotAfterChoiceMediaDeleted,
  patchSavedQuizQuestionsSnapshotAfterChoiceRemoved,
  patchSavedQuizQuestionsSnapshotAfterQuestionMediaDeleted,
  patchSavedQuizQuestionsSnapshotAfterQuestionRemoved,
  PREVIEW_BUTTON_DISABLED_TITLE,
  GAME_PREVIEW_BUTTON_CLASS,
  PREVIEW_UNSAVED_MESSAGE,
  PREVIEW_UNSAVED_TITLE,
  useHasUnsavedGameFormChanges,
} from "../shared/gameFormSnapshot";
import { useReportGameEditorLeaveGuard } from "../shared/GameEditorLeaveGuard";
import {
  createGameFieldErrorPopUp,
  findQuizQuestionMissingCorrectChoice,
  quizChoiceFieldTarget,
  quizQuestionFieldTarget,
  type GameStatusPopUpState,
  useGameFieldErrorHighlight,
} from "../shared/fieldErrorHighlight";
import { useLocale, useTranslations } from "next-intl";
import { getChoiceLabels } from "../shared/choiceLabels";
const MIN_QUESTIONS = 1;

type Answer = {
  id: number;
  text: string;
  isCorrect: boolean;
  imageFileName: string;
  imageFile: File | null;
  imageUrl: string | null;
  audioFileName: string;
  audioFile: File | null;
  audioUrl: string | null;
};

type Question = {
  id: number;
  text: string;
  shuffle_answer: boolean;
  skill_listening_speaking: boolean;
  skill_lang_nature: boolean;
  skill_reading: boolean;
  skill_writing: boolean;
  hasHint: boolean;
  hintText: string;
  hintImageFileName: string;
  hintImageFile: File | null;
  hintImageUrl: string | null;
  hintAudioFileName: string;
  hintAudioFile: File | null;
  hintAudioUrl: string | null;
  imageFileName: string;
  imageFile: File | null;
  imageUrl: string | null;
  audioFileName: string;
  audioFile: File | null;
  audioUrl: string | null;
  answers: Answer[];
};

type QuestionSkillKey =
  | "skill_listening_speaking"
  | "skill_lang_nature"
  | "skill_reading"
  | "skill_writing";

type QuestionSkillFlags = {
  skill_listening_speaking: boolean;
  skill_lang_nature: boolean;
  skill_reading: boolean;
  skill_writing: boolean;
};

const DEFAULT_GAME_SKILLS: QuestionSkillFlags = {
  skill_listening_speaking: false,
  skill_lang_nature: false,
  skill_reading: false,
  skill_writing: false,
};

type GameAudios = {
  audio_question: boolean;
  audio_choice: boolean;
  audio_assistant_voice: boolean;
  audio_background: boolean;
};

type GameAudiosKey = keyof GameAudios;

type StatusToggleDialogContent = {
  confirmTitle: string;
  confirmMessage: string;
  successTitle: string;
  successMessage: string;
};

const DEFAULT_GAME_AUDIOS: GameAudios = {
  audio_question: false,
  audio_choice: false,
  audio_assistant_voice: false,
  audio_background: false,
};

type UploadTarget = {
  key: string;
  file: File;
  fileName: string;
  sectionName: string;
  refId: string;
};

type QuestionMediaField =
  | "sound_question"
  | "image_question"
  | "hint"
  | "sound_hint"
  | "image_hint";

type ChoiceMediaField = "sound_choice" | "image_choice";

type PendingMediaDelete =
  | {
      target: "question";
      questionId: number;
      field: QuestionMediaField;
    }
  | {
      target: "choice";
      questionId: number;
      choiceId: number;
      field: ChoiceMediaField;
    };

type FlappyBirdProps = {
  createdGameData?: {
    id: number | string;
    exercise_name?: string;
    uuid?: string;
    description?: string | null;
    status?: boolean | "true" | "false" | number | string | null;
  } | null;
  initialGameDetail?: {
    game_info?: {
      id?: number | string;
      uuid?: string;
      exercise_name?: string;
      suggestion?: string | null;
      description?: string | null;
      status?: boolean | "true" | "false" | number | string | null;
    };
    questions?: Array<{
      id?: number;
      no?: number;
      question?: string;
      shuffle_answer?: boolean | "true" | "false" | null;
      hint?: string | null;
      image_question?: string | null;
      sound_question?: string | null;
      image_hint?: string | null;
      sound_hint?: string | null;
      skill_listening_speaking?: boolean | "true" | "false" | null;
      skill_lang_nature?: boolean | "true" | "false" | null;
      skill_reading?: boolean | "true" | "false" | null;
      skill_writing?: boolean | "true" | "false" | null;
      audio_question?: boolean | "true" | "false" | null;
      audio_choice?: boolean | "true" | "false" | null;
      audio_assistant_voice?: boolean | "true" | "false" | null;
      audio_background?: boolean | "true" | "false" | null;
      choices?: Array<{
        id?: number;
        choice?: string;
        is_correct?: boolean;
        image_choice?: string | null;
        sound_choice?: string | null;
      }>;
    }>;
  } | null;
  onCompleted?: () => void;
};

function getFileExtension(fileName: string) {
  const dotIndex = fileName.lastIndexOf(".");
  if (dotIndex < 0 || dotIndex === fileName.length - 1) {
    return "";
  }
  return fileName.slice(dotIndex);
}

function getFileNameFromPath(path: string | null | undefined) {
  if (!path) {
    return "";
  }

  const cleanPath = String(path);
  const bySlash = cleanPath.split("/");
  const candidate = bySlash[bySlash.length - 1] || "";
  return decodeURIComponent(candidate);
}

function buildUploadedMediaUrl(path: string | null | undefined) {
  if (!path) {
    return null;
  }

  const raw = String(path).trim();
  if (!raw) {
    return null;
  }

  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    return raw;
  }

  const baseUrl =
    process.env.NEXT_PUBLIC_BACKEND_URL ||
    "https://thaipilacreate.gforcesolution.com";
  const normalizedBaseUrl = baseUrl.replace(/\/$/, "");
  return `${normalizedBaseUrl}/api/${encodeURIComponent(raw)}`;
}

function normalizeSkillBoolean(value: unknown) {
  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") {
      return true;
    }
    if (normalized === "false") {
      return false;
    }
  }

  return false;
}

function normalizeStatus(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return normalized === "true" || normalized === "1";
}

function normalizeExpectedTime(value: unknown): string {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return "0";
  }
  return String(Math.floor(parsed));
}

function getPublishButtonClass(published: boolean): string {
  const base =
    "cursor-pointer inline-flex h-9 w-[116px] items-center justify-center gap-2 rounded-lg px-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-75";

  if (published) {
    return `${base} bg-[#E6F8EF]`;
  }

  return `${base} bg-[#E0E0E0]`;
}

function renderPublishStatusContent(
  published: boolean,
  labels: {
    publish: string;
    unpublish: string;
  },
  iconSize = 16,
) {
  if (published) {
    return (
      <>
        <span className="inline-flex shrink-0 items-center justify-center">
          <BiWorld size={iconSize} color="#16A34A" />
        </span>
        <span className="text-[#16A34A]">{labels.publish}</span>
      </>
    );
  }

  return (
    <>
      <span className="inline-flex shrink-0 items-center justify-center">
        <BiWorld size={iconSize} color="#3b3b3b" />
      </span>
      <span className="text-[#3b3b3b]">{labels.unpublish}</span>
    </>
  );
}

function getStatusToggleDialogContent(
  currentStatus: boolean,
  labels: {
    confirmPublish: string;
    publishPrompt: string;
    published: string;
    publishedMessage: string;
    confirmUnpublish: string;
    unpublishPrompt: string;
    unpublished: string;
    unpublishedMessage: string;
  },
): StatusToggleDialogContent {
  const nextStatus = !currentStatus;

  if (nextStatus) {
    return {
      confirmTitle: labels.confirmPublish,
      confirmMessage: labels.publishPrompt,
      successTitle: labels.published,
      successMessage: labels.publishedMessage,
    };
  }

  return {
    confirmTitle: labels.confirmUnpublish,
    confirmMessage: labels.unpublishPrompt,
    successTitle: labels.unpublished,
    successMessage: labels.unpublishedMessage,
  };
}

function extractQuestionSkills(
  question: Pick<Question, QuestionSkillKey>,
): QuestionSkillFlags {
  return {
    skill_listening_speaking: normalizeSkillBoolean(
      question.skill_listening_speaking,
    ),
    skill_lang_nature: normalizeSkillBoolean(question.skill_lang_nature),
    skill_reading: normalizeSkillBoolean(question.skill_reading),
    skill_writing: normalizeSkillBoolean(question.skill_writing),
  };
}

function extractGameAudiosFromUnknown(
  value: Partial<Record<GameAudiosKey, unknown>>,
): GameAudios {
  return {
    audio_question: normalizeSkillBoolean(value.audio_question),
    audio_choice: normalizeSkillBoolean(value.audio_choice),
    audio_assistant_voice: normalizeSkillBoolean(value.audio_assistant_voice),
    audio_background: normalizeSkillBoolean(value.audio_background),
  };
}

function normalizeGameSkillsOrNull(
  value: Partial<Record<QuestionSkillKey, unknown>>,
) {
  const listening = normalizeSkillBoolean(value.skill_listening_speaking);
  const langNature = normalizeSkillBoolean(value.skill_lang_nature);
  const reading = normalizeSkillBoolean(value.skill_reading);
  const writing = normalizeSkillBoolean(value.skill_writing);

  return {
    skill_listening_speaking: listening,
    skill_lang_nature: langNature,
    skill_reading: reading,
    skill_writing: writing,
  };
}

function extractGameSkillsFromUnknown(
  value: Partial<Record<QuestionSkillKey, unknown>>,
) {
  const normalized = normalizeGameSkillsOrNull(value);
  return normalized ?? { ...DEFAULT_GAME_SKILLS };
}

function areGameSkillsEqual(
  left: QuestionSkillFlags,
  right: QuestionSkillFlags,
) {
  return (
    left.skill_listening_speaking === right.skill_listening_speaking &&
    left.skill_lang_nature === right.skill_lang_nature &&
    left.skill_reading === right.skill_reading &&
    left.skill_writing === right.skill_writing
  );
}

function normalizeGameAudiosOrNull(
  value: Partial<Record<GameAudiosKey, unknown>>,
) {
  const audioQuestion = normalizeSkillBoolean(value.audio_question);
  const audioChoice = normalizeSkillBoolean(value.audio_choice);
  const audioAssistantVoice = normalizeSkillBoolean(
    value.audio_assistant_voice,
  );
  const audioBackground = normalizeSkillBoolean(value.audio_background);

  return {
    audio_question: audioQuestion,
    audio_choice: audioChoice,
    audio_assistant_voice: audioAssistantVoice,
    audio_background: audioBackground,
  };
}

function createEmptyAnswer(id: number): Answer {
  return {
    id,
    text: "",
    isCorrect: false,
    imageFileName: "",
    imageFile: null,
    imageUrl: null,
    audioFileName: "",
    audioFile: null,
    audioUrl: null,
  };
}

function createEmptyQuestion(id: number, answerIds: number[]): Question {
  return {
    id,
    text: "",
    shuffle_answer: false,
    skill_listening_speaking: false,
    skill_lang_nature: false,
    skill_reading: false,
    skill_writing: false,
    hasHint: false,
    hintText: "",
    hintImageFileName: "",
    hintImageFile: null,
    hintImageUrl: null,
    hintAudioFileName: "",
    hintAudioFile: null,
    hintAudioUrl: null,
    imageFileName: "",
    imageFile: null,
    imageUrl: null,
    audioFileName: "",
    audioFile: null,
    audioUrl: null,
    answers: answerIds.map((answerId) => createEmptyAnswer(answerId)),
  };
}

export default function FlappyBird({
  createdGameData,
  initialGameDetail,
  onCompleted,
}: FlappyBirdProps) {
  const locale = useLocale();
  const choiceLabels = getChoiceLabels(locale);
  const tFB = useTranslations("flappyBird");
  const tMain = useTranslations("main");
  const publishStatusLabels = {
    publish: tFB("publish"),
    unpublish: tFB("unpublish"),
  };
  const statusToggleDialogLabels = {
    confirmPublish: tFB("confirmPublish"),
    publishPrompt: tFB("publishPrompt"),
    published: tFB("published"),
    publishedMessage: tFB("publishedMessage"),
    confirmUnpublish: tFB("confirmUnpublish"),
    unpublishPrompt: tFB("unpublishPrompt"),
    unpublished: tFB("unpublished"),
    unpublishedMessage: tFB("unpublishedMessage"),
  };
  const nextQuestionIdRef = useRef(2);
  const nextAnswerIdRef = useRef(3);
  const initialExerciseNameRef = useRef(createdGameData?.exercise_name ?? "");
  const initialSuggestionRef = useRef("");
  const initialDescriptionRef = useRef("");
  const initialQuestionIdsRef = useRef<Set<number>>(new Set());
  const initialChoiceIdsByQuestionRef = useRef<Map<number, Set<number>>>(
    new Map(),
  );
  const initialQuestionSkillsRef = useRef<Map<number, QuestionSkillFlags>>(
    new Map(),
  );
  const initialGameAudiosRef = useRef<GameAudios>({ ...DEFAULT_GAME_AUDIOS });
  const initialGameSkillsRef = useRef<QuestionSkillFlags>({
    ...DEFAULT_GAME_SKILLS,
  });
  const savedFormSnapshotRef = useRef<ReturnType<
    typeof createQuizQuestionsGameSnapshot
  > | null>(null);

  const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(
    1,
  );
  const [exerciseName, setExerciseName] = useState(
    createdGameData?.exercise_name ?? "",
  );
  const [expectedTime, setExpectedTime] = useState("");
  const [suggestion, setSuggestion] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmSaveOpen, setIsConfirmSaveOpen] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(
    Array.isArray(initialGameDetail?.questions) &&
      initialGameDetail.questions.length > 0,
  );
  const [isPublished, setIsPublished] = useState(
    normalizeStatus(
      initialGameDetail?.game_info?.status ?? createdGameData?.status ?? false,
    ),
  );
  const [isStatusConfirmOpen, setIsStatusConfirmOpen] = useState(false);
  const [isStatusConfirming, setIsStatusConfirming] = useState(false);
  const [gameSkills, setGameSkills] = useState<QuestionSkillFlags>({
    ...DEFAULT_GAME_SKILLS,
  });
  const [statusPopUp, setStatusPopUp] = useState<GameStatusPopUpState | null>(
    null,
  );
  const {
    fieldErrorHighlightKey,
    clearFieldErrorHighlight,
    openStatusPopUp,
    closeStatusPopUp,
  } = useGameFieldErrorHighlight();
  const [previewIframeUrl, setPreviewIframeUrl] = useState<string | null>(null);
  const [gameAudios, setGameAudios] = useState<GameAudios>({
    ...DEFAULT_GAME_AUDIOS,
  });
  const [pendingMediaDelete, setPendingMediaDelete] =
    useState<PendingMediaDelete | null>(null);
  const [isDeletingMedia, setIsDeletingMedia] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([
    createEmptyQuestion(1, [1, 2]),
  ]);
  const canRemoveQuestion = questions.length > MIN_QUESTIONS;

  const previewUuid = String(
    initialGameDetail?.game_info?.uuid ?? createdGameData?.uuid ?? "",
  ).trim();
  useEffect(() => {
    setExerciseName(createdGameData?.exercise_name ?? "");
    setIsPublished(normalizeStatus(createdGameData?.status ?? false));
    initialExerciseNameRef.current = createdGameData?.exercise_name ?? "";
  }, [createdGameData?.exercise_name, createdGameData?.status]);

  useEffect(() => {
    if (initialGameDetail?.game_info?.id) {
      return;
    }

    const incomingDescription = String(createdGameData?.description ?? "");
    setDescription(incomingDescription);
    initialDescriptionRef.current = incomingDescription;
  }, [createdGameData?.description, initialGameDetail?.game_info?.id]);

  const applyLoadedGameDetail = (
    detail: FlappyBirdProps["initialGameDetail"],
  ) => {
    if (!detail?.game_info?.id) {
      const createdDescription = String(createdGameData?.description ?? "");
      setSuggestion("");
      setDescription(createdDescription);
      setExpectedTime("");
      initialSuggestionRef.current = "";
      initialDescriptionRef.current = createdDescription;
      initialQuestionIdsRef.current = new Set();
      initialChoiceIdsByQuestionRef.current = new Map();
      initialQuestionSkillsRef.current = new Map();
      initialGameAudiosRef.current = { ...DEFAULT_GAME_AUDIOS };
      setGameAudios({ ...DEFAULT_GAME_AUDIOS });
      setIsUpdateMode(false);
      savedFormSnapshotRef.current = createQuizQuestionsGameSnapshot(
        createdGameData?.exercise_name ?? "",
        "",
        createdDescription,
        { ...DEFAULT_GAME_SKILLS },
        { ...DEFAULT_GAME_AUDIOS },
        [createEmptyQuestion(1, [1, 2])],
      );
      return;
    }

    const gameInfo = detail.game_info;
    const incomingQuestions = Array.isArray(detail.questions)
      ? detail.questions
      : [];
    const firstQuestionRecord = incomingQuestions[0] as
      | Record<string, unknown>
      | undefined;

    const incomingExerciseName = String(gameInfo.exercise_name ?? "");
    const incomingSuggestion = String(gameInfo.suggestion ?? "");
    const incomingDescription = String(gameInfo.description ?? "");
    const incomingStatus = normalizeStatus(gameInfo.status);

    setExerciseName(incomingExerciseName);
    setSuggestion(incomingSuggestion);
    setDescription(incomingDescription);
    setExpectedTime(normalizeExpectedTime(firstQuestionRecord?.expected_time));
    setIsPublished(incomingStatus);
    initialExerciseNameRef.current = incomingExerciseName;
    initialSuggestionRef.current = incomingSuggestion;
    initialDescriptionRef.current = incomingDescription;

    const mappedQuestions: Question[] =
      incomingQuestions.length > 0
        ? incomingQuestions.map((question, questionIndex) => {
            const questionId = Number(
              question.id ?? question.no ?? questionIndex + 1,
            );
            const questionImagePath = question.image_question ?? null;
            const questionAudioPath = question.sound_question ?? null;
            const mappedAnswers: Answer[] = Array.isArray(question.choices)
              ? question.choices.map((choice, choiceIndex) => ({
                  id: Number(choice.id ?? choiceIndex + 1),
                  text: String(choice.choice ?? ""),
                  isCorrect: Boolean(choice.is_correct),
                  imageFileName: getFileNameFromPath(
                    choice.image_choice ?? null,
                  ),
                  imageFile: null,
                  imageUrl: buildUploadedMediaUrl(choice.image_choice ?? null),
                  audioFileName: getFileNameFromPath(
                    choice.sound_choice ?? null,
                  ),
                  audioFile: null,
                  audioUrl: buildUploadedMediaUrl(choice.sound_choice ?? null),
                }))
              : [];

            return {
              id: questionId,
              text: String(question.question ?? ""),
              shuffle_answer: normalizeSkillBoolean(question.shuffle_answer),
              skill_listening_speaking: normalizeSkillBoolean(
                question.skill_listening_speaking,
              ),
              skill_lang_nature: normalizeSkillBoolean(
                question.skill_lang_nature,
              ),
              skill_reading: normalizeSkillBoolean(question.skill_reading),
              skill_writing: normalizeSkillBoolean(question.skill_writing),
              hasHint: Boolean(
                String(question.hint ?? "").trim() ||
                question.image_hint ||
                question.sound_hint,
              ),
              hintText: String(question.hint ?? ""),
              hintImageFileName: getFileNameFromPath(
                question.image_hint ?? null,
              ),
              hintImageFile: null,
              hintImageUrl: buildUploadedMediaUrl(question.image_hint ?? null),
              hintAudioFileName: getFileNameFromPath(
                question.sound_hint ?? null,
              ),
              hintAudioFile: null,
              hintAudioUrl: buildUploadedMediaUrl(question.sound_hint ?? null),
              imageFileName: getFileNameFromPath(questionImagePath),
              imageFile: null,
              imageUrl: buildUploadedMediaUrl(questionImagePath),
              audioFileName: getFileNameFromPath(questionAudioPath),
              audioFile: null,
              audioUrl: buildUploadedMediaUrl(questionAudioPath),
              answers:
                mappedAnswers.length > 0
                  ? mappedAnswers
                  : [createEmptyAnswer(1), createEmptyAnswer(2)],
            };
          })
        : [createEmptyQuestion(1, [1, 2])];

    setQuestions(mappedQuestions);
    setSelectedQuestionId(mappedQuestions[0]?.id ?? null);

    const resolvedGameAudios = extractGameAudiosFromUnknown({
      audio_question: firstQuestionRecord?.audio_question,
      audio_choice: firstQuestionRecord?.audio_choice,
      audio_assistant_voice: firstQuestionRecord?.audio_assistant_voice,
      audio_background: firstQuestionRecord?.audio_background,
    });
    setGameAudios(resolvedGameAudios);
    initialGameAudiosRef.current = resolvedGameAudios;
    const resolvedGameSkills =
      incomingQuestions.length > 0
        ? extractQuestionSkills(
            firstQuestionRecord as Pick<Question, keyof Question>,
          )
        : { ...DEFAULT_GAME_SKILLS };
    setGameSkills(resolvedGameSkills);
    initialGameSkillsRef.current = resolvedGameSkills;

    const nextInitialQuestionIds = collectPersistedIdsFromApiRows(
      incomingQuestions,
      (question) => {
        const questionId = Number((question as { id?: number }).id);
        return Number.isInteger(questionId) && questionId > 0
          ? questionId
          : null;
      },
    );
    const nextInitialChoiceIdsByQuestion = new Map<number, Set<number>>();
    const nextInitialQuestionSkills = new Map<number, QuestionSkillFlags>();

    incomingQuestions.forEach((question) => {
      const record = question as {
        id?: number;
        choices?: Array<{ id?: number }>;
      };
      const questionId = Number(record.id);
      if (!nextInitialQuestionIds.has(questionId)) {
        return;
      }

      const choices = Array.isArray(record.choices) ? record.choices : [];
      nextInitialChoiceIdsByQuestion.set(
        questionId,
        collectPersistedIdsFromApiRows(choices, (choice) => {
          const choiceId = Number(choice.id);
          return Number.isInteger(choiceId) && choiceId > 0 ? choiceId : null;
        }),
      );

      const mappedQuestion = mappedQuestions.find(
        (item) => item.id === questionId,
      );
      if (mappedQuestion) {
        nextInitialQuestionSkills.set(
          questionId,
          extractQuestionSkills(mappedQuestion),
        );
      }
    });

    initialQuestionIdsRef.current = nextInitialQuestionIds;
    initialChoiceIdsByQuestionRef.current = nextInitialChoiceIdsByQuestion;
    initialQuestionSkillsRef.current = nextInitialQuestionSkills;

    const maxQuestionId = mappedQuestions.reduce(
      (max, question) => Math.max(max, question.id),
      0,
    );
    const maxAnswerId = mappedQuestions.reduce((max, question) => {
      const localMax = question.answers.reduce(
        (innerMax, answer) => Math.max(innerMax, answer.id),
        0,
      );
      return Math.max(max, localMax);
    }, 0);

    nextQuestionIdRef.current = maxQuestionId + 1;
    nextAnswerIdRef.current = maxAnswerId + 1;
    setIsUpdateMode(incomingQuestions.length > 0);
    savedFormSnapshotRef.current = createQuizQuestionsGameSnapshot(
      incomingExerciseName,
      incomingSuggestion,
      incomingDescription,
      resolvedGameSkills,
      resolvedGameAudios,
      mappedQuestions,
    );
  };

  useEffect(() => {
    applyLoadedGameDetail(initialGameDetail);
  }, [initialGameDetail]);

  const addQuestion = () => {
    const newQuestionId = nextQuestionIdRef.current;
    const firstAnswerId = nextAnswerIdRef.current;
    const secondAnswerId = nextAnswerIdRef.current + 1;

    nextQuestionIdRef.current += 1;
    nextAnswerIdRef.current += 2;

    setQuestions((previous) => [
      ...previous,
      createEmptyQuestion(newQuestionId, [firstAnswerId, secondAnswerId]),
    ]);

    setSelectedQuestionId(newQuestionId);
  };

  const removeQuestion = async (questionId: number) => {
    if (!canRemoveQuestion) {
      return;
    }
    if (isUpdateMode && initialQuestionIdsRef.current.has(questionId)) {
      const deleteQuestionResponse = await multipleChoiceAPI.deleteQuestion({
        questionId,
      });
      if (deleteQuestionResponse?.status === "failed") {
        setStatusPopUp({
          title: tFB("error"),
          message: tFB("deleteQuestionError"),
          type: "error",
        });
        return;
      }
      initialQuestionIdsRef.current.delete(questionId);
      initialChoiceIdsByQuestionRef.current.delete(questionId);
      initialQuestionSkillsRef.current.delete(questionId);
      patchSavedQuizQuestionsSnapshotAfterQuestionRemoved(
        savedFormSnapshotRef,
        questionId,
      );
    }

    setQuestions((previous) => {
      const filtered = previous.filter(
        (question) => question.id !== questionId,
      );

      if (filtered.length === 0) {
        setSelectedQuestionId(null);
        return filtered;
      }

      if (questionId === selectedQuestionId) {
        setSelectedQuestionId(filtered[0].id);
      }

      return filtered;
    });
  };

  const updateQuestionText = (questionId: number, value: string) => {
    if (
      fieldErrorHighlightKey ===
      quizQuestionFieldTarget(questionId, "question").highlightKey
    ) {
      clearFieldErrorHighlight();
    }
    setQuestions((previous) =>
      previous.map((question) =>
        question.id === questionId ? { ...question, text: value } : question,
      ),
    );
  };

  const enableQuestionHint = (questionId: number) => {
    setQuestions((previous) =>
      previous.map((question) =>
        question.id === questionId ? { ...question, hasHint: true } : question,
      ),
    );
  };

  const clearQuestionHint = (questionId: number) => {
    setQuestions((previous) =>
      previous.map((question) =>
        question.id === questionId
          ? {
              ...question,
              hasHint: false,
              hintText: "",
              hintImageFileName: "",
              hintImageFile: null,
              hintImageUrl: null,
              hintAudioFileName: "",
              hintAudioFile: null,
              hintAudioUrl: null,
            }
          : question,
      ),
    );
  };

  const getDeleteFieldLabel = (
    field: QuestionMediaField | ChoiceMediaField,
  ) => {
    switch (field) {
      case "image_question":
        return tFB("questionImage");
      case "sound_question":
        return tFB("questionAudio");
      case "image_hint":
        return tFB("hintImage");
      case "sound_hint":
        return tFB("hintAudio");
      case "hint":
        return tFB("hint");
      case "image_choice":
        return tFB("optionImage");
      case "sound_choice":
        return tFB("optionAudio");
      default:
        return tFB("dataLabel");
    }
  };

  const requestDeleteQuestionMediaField = (
    questionId: number,
    field: QuestionMediaField,
  ) => {
    setPendingMediaDelete({
      target: "question",
      questionId,
      field,
    });
  };

  const requestDeleteChoiceMediaField = (
    questionId: number,
    choiceId: number,
    field: ChoiceMediaField,
  ) => {
    setPendingMediaDelete({
      target: "choice",
      questionId,
      choiceId,
      field,
    });
  };

  const clearQuestionMediaFieldFromState = (
    questionId: number,
    field: QuestionMediaField,
  ) => {
    if (field === "hint") {
      clearQuestionHint(questionId);
      return;
    }

    setQuestions((previous) =>
      previous.map((question) => {
        if (question.id !== questionId) {
          return question;
        }

        if (field === "image_question") {
          return {
            ...question,
            imageFileName: "",
            imageFile: null,
            imageUrl: null,
          };
        }

        if (field === "sound_question") {
          return {
            ...question,
            audioFileName: "",
            audioFile: null,
            audioUrl: null,
          };
        }

        if (field === "image_hint") {
          return {
            ...question,
            hintImageFileName: "",
            hintImageFile: null,
            hintImageUrl: null,
          };
        }

        return {
          ...question,
          hintAudioFileName: "",
          hintAudioFile: null,
          hintAudioUrl: null,
        };
      }),
    );
  };

  const clearChoiceMediaFieldFromState = (
    questionId: number,
    choiceId: number,
    field: ChoiceMediaField,
  ) => {
    setQuestions((previous) =>
      previous.map((question) => {
        if (question.id !== questionId) {
          return question;
        }

        return {
          ...question,
          answers: question.answers.map((answer) => {
            if (answer.id !== choiceId) {
              return answer;
            }

            if (field === "image_choice") {
              return {
                ...answer,
                imageFileName: "",
                imageFile: null,
                imageUrl: null,
              };
            }

            return {
              ...answer,
              audioFileName: "",
              audioFile: null,
              audioUrl: null,
            };
          }),
        };
      }),
    );
  };

  const confirmDeleteMediaField = async () => {
    if (!pendingMediaDelete) {
      return;
    }

    try {
      setIsDeletingMedia(true);

      if (pendingMediaDelete.target === "question") {
        const { questionId, field } = pendingMediaDelete;
        const shouldCallDeleteApi =
          isUpdateMode && initialQuestionIdsRef.current.has(questionId);

        if (shouldCallDeleteApi) {
          const response =
            await multipleChoiceAPI.hardDeleteQuestionMediaFileAndHint({
              questionId,
              field,
            });

          if (response?.status === "failed") {
            setStatusPopUp({
              title: tFB("error"),
              message: `${tFB("delete")} ${getDeleteFieldLabel(field)} ${tFB("error")}`,
              type: "error",
            });
            return;
          }

          patchSavedQuizQuestionsSnapshotAfterQuestionMediaDeleted(
            savedFormSnapshotRef,
            questionId,
            field,
          );
        }

        clearQuestionMediaFieldFromState(questionId, field);
        setPendingMediaDelete(null);
        return;
      }

      const { questionId, choiceId, field } = pendingMediaDelete;
      const initialChoiceIds =
        initialChoiceIdsByQuestionRef.current.get(questionId);
      const shouldCallDeleteApi =
        isUpdateMode && Boolean(initialChoiceIds?.has(choiceId));

      if (shouldCallDeleteApi) {
        const response =
          await multipleChoiceAPI.hardDeleteChoiceMediaFileAndHint({
            choiceId,
            field,
          });

        if (response?.status === "failed") {
          setStatusPopUp({
            title: tFB("error"),
            message: `${tFB("delete")} ${getDeleteFieldLabel(field)} ${tFB("error")}`,
            type: "error",
          });
          return;
        }

        patchSavedQuizQuestionsSnapshotAfterChoiceMediaDeleted(
          savedFormSnapshotRef,
          questionId,
          choiceId,
          field,
        );
      }

      clearChoiceMediaFieldFromState(questionId, choiceId, field);
      setPendingMediaDelete(null);
    } finally {
      setIsDeletingMedia(false);
    }
  };

  const updateQuestionHintText = (questionId: number, value: string) => {
    setQuestions((previous) =>
      previous.map((question) =>
        question.id === questionId
          ? { ...question, hasHint: true, hintText: value }
          : question,
      ),
    );
  };

  const updateGameSkill = (skillKey: QuestionSkillKey, checked: boolean) => {
    setGameSkills((previous) => ({ ...previous, [skillKey]: checked }));
  };

  const updateGameAudio = (audioKey: GameAudiosKey, checked: boolean) => {
    setGameAudios((previous) => ({
      ...previous,
      [audioKey]: checked,
    }));
  };

  const updateAnswerText = (
    questionId: number,
    answerId: number,
    value: string,
  ) => {
    if (
      fieldErrorHighlightKey ===
      quizChoiceFieldTarget(questionId, answerId).highlightKey
    ) {
      clearFieldErrorHighlight();
    }
    setQuestions((previous) =>
      previous.map((question) =>
        question.id === questionId
          ? {
              ...question,
              answers: question.answers.map((answer) =>
                answer.id === answerId ? { ...answer, text: value } : answer,
              ),
            }
          : question,
      ),
    );
  };

  const updateQuestionShuffleAnswer = (
    questionId: number,
    checked: boolean,
  ) => {
    setQuestions((previous) =>
      previous.map((question) =>
        question.id === questionId
          ? { ...question, shuffle_answer: checked }
          : question,
      ),
    );
  };

  const updateQuestionImageFile = (questionId: number, file: File) => {
    setQuestions((previous) =>
      previous.map((question) =>
        question.id === questionId
          ? {
              ...question,
              imageFileName: file.name,
              imageFile: file,
              imageUrl: null,
            }
          : question,
      ),
    );
  };

  const updateQuestionAudioFile = (questionId: number, file: File) => {
    setQuestions((previous) =>
      previous.map((question) =>
        question.id === questionId
          ? {
              ...question,
              audioFileName: file.name,
              audioFile: file,
              audioUrl: null,
            }
          : question,
      ),
    );
  };

  const updateQuestionHintImageFile = (questionId: number, file: File) => {
    setQuestions((previous) =>
      previous.map((question) =>
        question.id === questionId
          ? {
              ...question,
              hasHint: true,
              hintImageFileName: file.name,
              hintImageFile: file,
              hintImageUrl: null,
            }
          : question,
      ),
    );
  };

  const updateQuestionHintAudioFile = (questionId: number, file: File) => {
    setQuestions((previous) =>
      previous.map((question) =>
        question.id === questionId
          ? {
              ...question,
              hasHint: true,
              hintAudioFileName: file.name,
              hintAudioFile: file,
              hintAudioUrl: null,
            }
          : question,
      ),
    );
  };

  const updateAnswerImageFile = (
    questionId: number,
    answerId: number,
    file: File,
  ) => {
    setQuestions((previous) =>
      previous.map((question) =>
        question.id === questionId
          ? {
              ...question,
              answers: question.answers.map((answer) =>
                answer.id === answerId
                  ? {
                      ...answer,
                      imageFileName: file.name,
                      imageFile: file,
                      imageUrl: null,
                    }
                  : answer,
              ),
            }
          : question,
      ),
    );
  };

  const updateAnswerAudioFile = (
    questionId: number,
    answerId: number,
    file: File,
  ) => {
    setQuestions((previous) =>
      previous.map((question) =>
        question.id === questionId
          ? {
              ...question,
              answers: question.answers.map((answer) =>
                answer.id === answerId
                  ? {
                      ...answer,
                      audioFileName: file.name,
                      audioFile: file,
                      audioUrl: null,
                    }
                  : answer,
              ),
            }
          : question,
      ),
    );
  };

  const toggleAnswerCorrect = (questionId: number, answerId: number) => {
    if (
      fieldErrorHighlightKey ===
      quizQuestionFieldTarget(questionId, "answers").highlightKey
    ) {
      clearFieldErrorHighlight();
    }
    setQuestions((previous) =>
      previous.map((question) =>
        question.id === questionId
          ? {
              ...question,
              answers: question.answers.map((answer) =>
                answer.id === answerId
                  ? { ...answer, isCorrect: !answer.isCorrect }
                  : answer,
              ),
            }
          : question,
      ),
    );
  };

  const addAnswer = (questionId: number) => {
    const newAnswerId = nextAnswerIdRef.current;
    nextAnswerIdRef.current += 1;

    setQuestions((previous) =>
      previous.map((question) => {
        if (
          question.id !== questionId ||
          question.answers.length >= choiceLabels.length
        ) {
          return question;
        }

        return {
          ...question,
          answers: [...question.answers, createEmptyAnswer(newAnswerId)],
        };
      }),
    );
  };

  const collectUploadTargets = (sourceQuestions: Question[]) => {
    const targets: UploadTarget[] = [];

    const getTimestamp = () => Date.now();

    sourceQuestions.forEach((question, questionIndex) => {
      if (question.imageFile && !question.imageUrl) {
        const timestamp = getTimestamp();
        const ext = getFileExtension(question.imageFile.name);
        targets.push({
          key: `q-${questionIndex}-image_question`,
          file: question.imageFile,
          fileName: `image_question_q${question.id}_${timestamp}${ext}`,
          sectionName: "image_question",
          refId: `q_${question.id}`,
        });
      }

      if (question.audioFile && !question.audioUrl) {
        const timestamp = getTimestamp();
        const ext = getFileExtension(question.audioFile.name);
        targets.push({
          key: `q-${questionIndex}-sound_question`,
          file: question.audioFile,
          fileName: `sound_question_q${question.id}_${timestamp}${ext}`,
          sectionName: "sound_question",
          refId: `q_${question.id}`,
        });
      }

      if (question.hintImageFile && !question.hintImageUrl) {
        const timestamp = getTimestamp();
        const ext = getFileExtension(question.hintImageFile.name);
        targets.push({
          key: `q-${questionIndex}-image_hint`,
          file: question.hintImageFile,
          fileName: `image_hint_q${question.id}_${timestamp}${ext}`,
          sectionName: "image_hint",
          refId: `q_${question.id}`,
        });
      }

      if (question.hintAudioFile && !question.hintAudioUrl) {
        const timestamp = getTimestamp();
        const ext = getFileExtension(question.hintAudioFile.name);
        targets.push({
          key: `q-${questionIndex}-sound_hint`,
          file: question.hintAudioFile,
          fileName: `sound_hint_q${question.id}_${timestamp}${ext}`,
          sectionName: "sound_hint",
          refId: `q_${question.id}`,
        });
      }

      question.answers.forEach((answer, answerIndex) => {
        if (answer.imageFile && !answer.imageUrl) {
          const timestamp = getTimestamp();
          const ext = getFileExtension(answer.imageFile.name);
          targets.push({
            key: `q-${questionIndex}-a-${answerIndex}-image_choice`,
            file: answer.imageFile,
            fileName: `image_choice_q${question.id}_c${answer.id}_${timestamp}${ext}`,
            sectionName: "image_choice",
            refId: `choice_${question.id}_${answer.id}`,
          });
        }

        if (answer.audioFile && !answer.audioUrl) {
          const timestamp = getTimestamp();
          const ext = getFileExtension(answer.audioFile.name);
          targets.push({
            key: `q-${questionIndex}-a-${answerIndex}-sound_choice`,
            file: answer.audioFile,
            fileName: `sound_choice_q${question.id}_c${answer.id}_${timestamp}${ext}`,
            sectionName: "sound_choice",
            refId: `choice_${question.id}_${answer.id}`,
          });
        }
      });
    });

    return targets;
  };

  const uploadPendingMedia = async (targets: UploadTarget[]) => {
    const uploadedUrlByKey = new Map<string, string>();

    if (targets.length === 0) {
      return uploadedUrlByKey;
    }

    const uploadResults = await Promise.allSettled(
      targets.map((target) =>
        uploadAPI
          .uploadMedia({
            file: target.file,
            fileName: target.fileName,
            sectionName: target.sectionName,
            ref_id: target.refId,
          })
          .then((response) => ({ target, response })),
      ),
    );

    for (const result of uploadResults) {
      if (result.status === "rejected") {
        throw new Error("upload_failed");
      }

      const response = result.value.response as {
        status?: string;
        url?: string;
      };
      if (response?.status === "failed" || !response?.url) {
        throw new Error("upload_failed");
      }

      uploadedUrlByKey.set(result.value.target.key, response.url);
    }

    return uploadedUrlByKey;
  };

  const applyUploadedUrlsToState = (
    sourceQuestions: Question[],
    uploadedUrlByKey: Map<string, string>,
  ) => {
    return sourceQuestions.map((question, questionIndex) => ({
      ...question,
      imageUrl:
        uploadedUrlByKey.get(`q-${questionIndex}-image_question`) ??
        question.imageUrl,
      imageFile: uploadedUrlByKey.get(`q-${questionIndex}-image_question`)
        ? null
        : question.imageFile,
      audioUrl:
        uploadedUrlByKey.get(`q-${questionIndex}-sound_question`) ??
        question.audioUrl,
      audioFile: uploadedUrlByKey.get(`q-${questionIndex}-sound_question`)
        ? null
        : question.audioFile,
      hintImageUrl:
        uploadedUrlByKey.get(`q-${questionIndex}-image_hint`) ??
        question.hintImageUrl,
      hintImageFile: uploadedUrlByKey.get(`q-${questionIndex}-image_hint`)
        ? null
        : question.hintImageFile,
      hintAudioUrl:
        uploadedUrlByKey.get(`q-${questionIndex}-sound_hint`) ??
        question.hintAudioUrl,
      hintAudioFile: uploadedUrlByKey.get(`q-${questionIndex}-sound_hint`)
        ? null
        : question.hintAudioFile,
      answers: question.answers.map((answer, answerIndex) => ({
        ...answer,
        imageUrl:
          uploadedUrlByKey.get(
            `q-${questionIndex}-a-${answerIndex}-image_choice`,
          ) ?? answer.imageUrl,
        imageFile: uploadedUrlByKey.get(
          `q-${questionIndex}-a-${answerIndex}-image_choice`,
        )
          ? null
          : answer.imageFile,
        audioUrl:
          uploadedUrlByKey.get(
            `q-${questionIndex}-a-${answerIndex}-sound_choice`,
          ) ?? answer.audioUrl,
        audioFile: uploadedUrlByKey.get(
          `q-${questionIndex}-a-${answerIndex}-sound_choice`,
        )
          ? null
          : answer.audioFile,
      })),
    }));
  };

  const removeAnswer = async (questionId: number, answerId: number) => {
    if (isUpdateMode) {
      const initialChoiceIds =
        initialChoiceIdsByQuestionRef.current.get(questionId);
      if (initialChoiceIds?.has(answerId)) {
        const deleteChoiceResponse = await multipleChoiceAPI.deleteChoice({
          questionId,
          choiceIds: [answerId],
        });
        if (deleteChoiceResponse?.status === "failed") {
          setStatusPopUp({
            title: tFB("error"),
            message: tFB("deleteOptionError"),
            type: "error",
          });
          return;
        }
        initialChoiceIds.delete(answerId);
        patchSavedQuizQuestionsSnapshotAfterChoiceRemoved(
          savedFormSnapshotRef,
          questionId,
          answerId,
        );
      }
    }

    setQuestions((previous) =>
      previous.map((question) =>
        question.id === questionId
          ? {
              ...question,
              answers: question.answers.filter(
                (answer) => answer.id !== answerId,
              ),
            }
          : question,
      ),
    );
  };

  const moveQuestion = (questionId: number, direction: -1 | 1) => {
    setQuestions((previous) => {
      const currentIndex = previous.findIndex(
        (question) => question.id === questionId,
      );

      if (currentIndex < 0) {
        return previous;
      }

      const nextIndex = currentIndex + direction;
      if (nextIndex < 0 || nextIndex >= previous.length) {
        return previous;
      }

      const reordered = [...previous];
      const [movedQuestion] = reordered.splice(currentIndex, 1);
      reordered.splice(nextIndex, 0, movedQuestion);
      return reordered;
    });
  };

  const moveQuestionUp = (questionId: number) => {
    moveQuestion(questionId, -1);
  };

  const moveQuestionDown = (questionId: number) => {
    moveQuestion(questionId, 1);
  };

  const handleSubmitFlappyBird = async () => {
    if (!createdGameData?.id) {
      setStatusPopUp({
        title: tFB("error"),
        message: tFB("noRecentGameData"),
        type: "error",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const trimmedExerciseName = exerciseName.trim();
      const trimmedSuggestion = suggestion.trim();
      const trimmedDescription = description.trim();
      const trimmedExpectedTime = expectedTime.trim();
      const expectedTimeValue = Number(trimmedExpectedTime);
      if (
        trimmedExpectedTime === "" ||
        !Number.isInteger(expectedTimeValue) ||
        expectedTimeValue < 0
      ) {
        setStatusPopUp({
          title: tFB("error"),
          message: tMain("expectedTimeInvalid"),
          type: "error",
        });
        return;
      }
      const shouldUpdateExerciseName =
        trimmedExerciseName !== initialExerciseNameRef.current.trim();
      const shouldUpdateSuggestion =
        trimmedSuggestion !== initialSuggestionRef.current.trim();
      const shouldUpdateDescription =
        trimmedDescription !== initialDescriptionRef.current.trim();

      if (
        shouldUpdateExerciseName ||
        shouldUpdateSuggestion ||
        shouldUpdateDescription
      ) {
        const updateResponse =
          await multipleChoiceAPI.updateExerciseNameAndSuggestion({
            gameId: createdGameData.id,
            ...(shouldUpdateExerciseName
              ? { exercise_name: trimmedExerciseName }
              : {}),
            ...(shouldUpdateSuggestion
              ? { suggestion: trimmedSuggestion }
              : {}),
            ...(shouldUpdateDescription
              ? { description: trimmedDescription }
              : {}),
          });

        if (updateResponse?.status === "failed") {
          setStatusPopUp({
            title: tFB("error"),
            message: tFB("updateError"),
            type: "error",
          });
          return;
        }

        initialExerciseNameRef.current = trimmedExerciseName;
        initialSuggestionRef.current = trimmedSuggestion;
        initialDescriptionRef.current = trimmedDescription;
        setExerciseName(trimmedExerciseName);
        setSuggestion(trimmedSuggestion);
        setDescription(trimmedDescription);
      }

      const uploadTargets = collectUploadTargets(questions);
      const uploadedUrlByKey = await uploadPendingMedia(uploadTargets);
      const questionsWithResolvedUrls = applyUploadedUrlsToState(
        questions,
        uploadedUrlByKey,
      );

      if (
        !Array.isArray(questionsWithResolvedUrls) ||
        questionsWithResolvedUrls.length === 0
      ) {
        openStatusPopUp(
          setStatusPopUp,
          createGameFieldErrorPopUp(
            tFB("error"),
            tFB("minQuestionsRequired"),
            quizQuestionFieldTarget(questions[0]?.id ?? 1),
          ),
        );
        return;
      }

      const payloadQuestions: Array<{
        no: number;
        expected_time: number;
        question: string;
        shuffle_answer: boolean;
        hint: string | null;
        image_question: string | null;
        sound_question: string | null;
        image_hint: string | null;
        sound_hint: string | null;
        skill_listening_speaking: boolean;
        skill_lang_nature: boolean;
        skill_reading: boolean;
        skill_writing: boolean;
        audio_question: boolean;
        audio_choice: boolean;
        audio_assistant_voice: boolean;
        audio_background: boolean;
        choices: Array<{
          choice: string;
          is_correct: boolean;
          image_choice: string | null;
          sound_choice: string | null;
        }>;
      }> = [];
      const normalizedGameAudios = extractGameAudiosFromUnknown(gameAudios);
      const normalizedGameSkills = normalizeGameSkillsOrNull(gameSkills);
      if (!normalizedGameSkills) {
        setStatusPopUp({
          title: tFB("error"),
          message: tFB("invalidData"),
          type: "error",
        });
        return;
      }

      for (
        let questionIndex = 0;
        questionIndex < questionsWithResolvedUrls.length;
        questionIndex += 1
      ) {
        const question = questionsWithResolvedUrls[questionIndex];
        const normalizedQuestionText = question.text.trim();
        const normalizedHintText = question.hasHint
          ? question.hintText.trim()
          : "";
        const normalizedSkills = extractQuestionSkills(question);

        const hasQuestionMedia =
          Boolean(normalizedQuestionText) ||
          Boolean(question.imageUrl) ||
          Boolean(question.audioUrl);
        if (!hasQuestionMedia) {
          setSelectedQuestionId(question.id);
          openStatusPopUp(
            setStatusPopUp,
            createGameFieldErrorPopUp(
              tFB("error"),
              `${tFB("questionNumber")} ${questionIndex + 1} ${tFB("minContentRequired")}`,
              quizQuestionFieldTarget(question.id, "question"),
            ),
          );
          return;
        }

        if (!Array.isArray(question.answers) || question.answers.length === 0) {
          setSelectedQuestionId(question.id);
          openStatusPopUp(
            setStatusPopUp,
            createGameFieldErrorPopUp(
              tFB("error"),
              `${tFB("questionNumber")} ${questionIndex + 1} ${tFB("minOptionsRequired")}`,
              quizQuestionFieldTarget(question.id, "question"),
            ),
          );
          return;
        }

        const normalizedChoices: Array<{
          choice: string;
          is_correct: boolean;
          image_choice: string | null;
          sound_choice: string | null;
        }> = [];

        for (
          let answerIndex = 0;
          answerIndex < question.answers.length;
          answerIndex += 1
        ) {
          const answer = question.answers[answerIndex];
          const normalizedChoiceText = answer.text.trim();

          const hasChoiceMedia =
            Boolean(normalizedChoiceText) ||
            Boolean(answer.imageUrl) ||
            Boolean(answer.audioUrl);
          if (!hasChoiceMedia) {
            setSelectedQuestionId(question.id);
            openStatusPopUp(
              setStatusPopUp,
              createGameFieldErrorPopUp(
                tFB("error"),
                `${tFB("optionNumber")} ${answerIndex + 1} ${tFB("ofQuestionNumber")} ${questionIndex + 1} ${tFB("minContentRequired")}`,
                quizChoiceFieldTarget(question.id, answer.id),
              ),
            );
            return;
          }

          normalizedChoices.push({
            choice: normalizedChoiceText,
            is_correct: normalizeSkillBoolean(answer.isCorrect),
            image_choice: answer.imageUrl,
            sound_choice: answer.audioUrl,
          });
        }

        const hasCorrectChoice = question.answers.some((answer) =>
          normalizeSkillBoolean(answer.isCorrect),
        );
        if (!hasCorrectChoice) {
          setSelectedQuestionId(question.id);
          openStatusPopUp(
            setStatusPopUp,
            createGameFieldErrorPopUp(
              tFB("error"),
              `${tFB("questionNumber")} ${questionIndex + 1} ${tFB("mustSelectCorrectAnswer")}`,
              quizQuestionFieldTarget(question.id, "answers"),
            ),
          );
          return;
        }

        payloadQuestions.push({
          no: questionIndex + 1,
          expected_time: expectedTimeValue,
          question: normalizedQuestionText,
          shuffle_answer: normalizeSkillBoolean(question.shuffle_answer),
          hint: normalizedHintText || null,
          image_question: question.imageUrl,
          sound_question: question.audioUrl,
          image_hint: question.hasHint ? question.hintImageUrl : null,
          sound_hint: question.hasHint ? question.hintAudioUrl : null,
          skill_listening_speaking:
            normalizedGameSkills.skill_listening_speaking,
          skill_lang_nature: normalizedGameSkills.skill_lang_nature,
          skill_reading: normalizedGameSkills.skill_reading,
          skill_writing: normalizedGameSkills.skill_writing,
          audio_question: normalizedGameAudios.audio_question,
          audio_choice: normalizedGameAudios.audio_choice,
          audio_assistant_voice: normalizedGameAudios.audio_assistant_voice,
          audio_background: normalizedGameAudios.audio_background,
          choices: normalizedChoices,
        });
      }
      const response = await multipleChoiceAPI.createMultipleChoice({
        gameId: createdGameData.id,
        questions: payloadQuestions,
      });

      if (response?.status === "failed") {
        setStatusPopUp({
          title: tFB("error"),
          message: tFB("saveError"),
          type: "error",
        });
        return;
      }

      setQuestions(questionsWithResolvedUrls);

      if (previewUuid) {
        const latestGameDetail =
          await mainAPI.getGameinfoAndQuestionByUuid(previewUuid);
        if (
          latestGameDetail &&
          (latestGameDetail as { status?: string }).status !== "failed"
        ) {
          applyLoadedGameDetail(
            latestGameDetail as FlappyBirdProps["initialGameDetail"],
          );
        } else {
          setIsUpdateMode(true);
          initialQuestionIdsRef.current = new Set(
            questionsWithResolvedUrls.map((question) => question.id),
          );
          initialChoiceIdsByQuestionRef.current = new Map(
            questionsWithResolvedUrls.map((question) => [
              question.id,
              new Set(question.answers.map((answer) => answer.id)),
            ]),
          );
          initialQuestionSkillsRef.current = new Map(
            questionsWithResolvedUrls.map((question) => [
              question.id,
              extractQuestionSkills(question),
            ]),
          );
          initialGameAudiosRef.current = normalizedGameAudios;
          initialGameSkillsRef.current = normalizedGameSkills;
          commitSavedQuizQuestionsGameSnapshot(
            savedFormSnapshotRef,
            trimmedExerciseName,
            trimmedSuggestion,
            trimmedDescription,
            normalizedGameSkills,
            normalizedGameAudios,
            questionsWithResolvedUrls,
          );
        }
      } else {
        setIsUpdateMode(true);
        initialQuestionIdsRef.current = new Set(
          questionsWithResolvedUrls.map((question) => question.id),
        );
        initialChoiceIdsByQuestionRef.current = new Map(
          questionsWithResolvedUrls.map((question) => [
            question.id,
            new Set(question.answers.map((answer) => answer.id)),
          ]),
        );
        initialQuestionSkillsRef.current = new Map(
          questionsWithResolvedUrls.map((question) => [
            question.id,
            extractQuestionSkills(question),
          ]),
        );
        initialGameAudiosRef.current = normalizedGameAudios;
        initialGameSkillsRef.current = normalizedGameSkills;
        commitSavedQuizQuestionsGameSnapshot(
          savedFormSnapshotRef,
          trimmedExerciseName,
          trimmedSuggestion,
          trimmedDescription,
          normalizedGameSkills,
          normalizedGameAudios,
          questionsWithResolvedUrls,
        );
      }

      setStatusPopUp(buildGameSuccessStatus(tFB("flappyBird"), "create"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateFlappyBird = async () => {
    if (!createdGameData?.id) {
      setStatusPopUp({
        title: tFB("error"),
        message: tFB("noGameDataToUpdate"),
        type: "error",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const trimmedExerciseName = exerciseName.trim();
      const trimmedSuggestion = suggestion.trim();
      const trimmedDescription = description.trim();
      const trimmedExpectedTime = expectedTime.trim();
      const expectedTimeValue = Number(trimmedExpectedTime);
      if (
        trimmedExpectedTime === "" ||
        !Number.isInteger(expectedTimeValue) ||
        expectedTimeValue < 0
      ) {
        setStatusPopUp({
          title: tFB("error"),
          message: tMain("expectedTimeInvalid"),
          type: "error",
        });
        return;
      }
      const shouldUpdateExerciseName =
        trimmedExerciseName !== initialExerciseNameRef.current.trim();
      const shouldUpdateSuggestion =
        trimmedSuggestion !== initialSuggestionRef.current.trim();
      const shouldUpdateDescription =
        trimmedDescription !== initialDescriptionRef.current.trim();

      if (
        shouldUpdateExerciseName ||
        shouldUpdateSuggestion ||
        shouldUpdateDescription
      ) {
        const updateResponse =
          await multipleChoiceAPI.updateExerciseNameAndSuggestion({
            gameId: createdGameData.id,
            ...(shouldUpdateExerciseName
              ? { exercise_name: trimmedExerciseName }
              : {}),
            ...(shouldUpdateSuggestion
              ? { suggestion: trimmedSuggestion }
              : {}),
            ...(shouldUpdateDescription
              ? { description: trimmedDescription }
              : {}),
          });

        if (updateResponse?.status === "failed") {
          setStatusPopUp({
            title: tFB("error"),
            message: tFB("updateError"),
            type: "error",
          });
          return;
        }

        initialExerciseNameRef.current = trimmedExerciseName;
        initialSuggestionRef.current = trimmedSuggestion;
        initialDescriptionRef.current = trimmedDescription;
        setExerciseName(trimmedExerciseName);
        setSuggestion(trimmedSuggestion);
        setDescription(trimmedDescription);
      }

      const uploadTargets = collectUploadTargets(questions);
      const uploadedUrlByKey = await uploadPendingMedia(uploadTargets);
      const questionsWithResolvedUrls = applyUploadedUrlsToState(
        questions,
        uploadedUrlByKey,
      );
      let questionsWithSyncedChoiceIds = questionsWithResolvedUrls;

      const missingCorrectChoiceOnUpdate = findQuizQuestionMissingCorrectChoice(
        questionsWithResolvedUrls,
      );
      if (missingCorrectChoiceOnUpdate) {
        const { questionIndex, question } = missingCorrectChoiceOnUpdate;
        setSelectedQuestionId(question.id);
        openStatusPopUp(
          setStatusPopUp,
          createGameFieldErrorPopUp(
            tFB("error"),
            `${tFB("questionNumber")} ${questionIndex + 1} ${tFB("mustSelectCorrectAnswer")}`,
            quizQuestionFieldTarget(question.id, "answers"),
          ),
        );
        return;
      }

      const currentGameAudios = extractGameAudiosFromUnknown(gameAudios);
      const initialGameAudios = initialGameAudiosRef.current;
      const changedAudioPayload: Partial<GameAudios> = {};
      if (
        initialGameAudios.audio_question !== currentGameAudios.audio_question
      ) {
        changedAudioPayload.audio_question = currentGameAudios.audio_question;
      }
      if (initialGameAudios.audio_choice !== currentGameAudios.audio_choice) {
        changedAudioPayload.audio_choice = currentGameAudios.audio_choice;
      }
      if (
        initialGameAudios.audio_assistant_voice !==
        currentGameAudios.audio_assistant_voice
      ) {
        changedAudioPayload.audio_assistant_voice =
          currentGameAudios.audio_assistant_voice;
      }
      if (
        initialGameAudios.audio_background !==
        currentGameAudios.audio_background
      ) {
        changedAudioPayload.audio_background =
          currentGameAudios.audio_background;
      }

      const currentQuestionIds = new Set<number>(
        questionsWithResolvedUrls.map((question) => question.id),
      );

      const deletedQuestionIds = Array.from(
        initialQuestionIdsRef.current,
      ).filter((questionId) => !currentQuestionIds.has(questionId));

      for (const questionId of deletedQuestionIds) {
        const deleteQuestionResponse = await multipleChoiceAPI.deleteQuestion({
          questionId,
        });

        if (deleteQuestionResponse?.status === "failed") {
          setStatusPopUp({
            title: tFB("error"),
            message: `${tFB("deleteQuestionErrorId")} ${questionId} ${tFB("error")}`,
            type: "error",
          });
          return;
        }
      }

      for (const question of questionsWithResolvedUrls) {
        const initialChoiceIds = initialChoiceIdsByQuestionRef.current.get(
          question.id,
        );
        if (!initialChoiceIds || initialChoiceIds.size === 0) {
          continue;
        }

        const currentChoiceIds = new Set<number>(
          question.answers.map((answer) => answer.id),
        );
        const deletedChoiceIds = Array.from(initialChoiceIds).filter(
          (choiceId) => !currentChoiceIds.has(choiceId),
        );

        if (deletedChoiceIds.length === 0) {
          continue;
        }

        const deleteChoiceResponse = await multipleChoiceAPI.deleteChoice({
          questionId: question.id,
          choiceIds: deletedChoiceIds,
        });

        if (deleteChoiceResponse?.status === "failed") {
          setStatusPopUp({
            title: tFB("error"),
            message: `${tFB("deleteOptionErrorId")} ${question.id} ${tFB("error")}`,
            type: "error",
          });
          return;
        }
      }

      const normalizedGameAudios = normalizeGameAudiosOrNull(gameAudios);
      if (!normalizedGameAudios) {
        setStatusPopUp({
          title: tFB("error"),
          message: tFB("invalidData"),
          type: "error",
        });
        return;
      }

      const normalizedGameSkills = normalizeGameSkillsOrNull(gameSkills);
      if (!normalizedGameSkills) {
        setStatusPopUp({
          title: tFB("error"),
          message: tFB("invalidData"),
          type: "error",
        });
        return;
      }

      const globalChangedSkillPayload: Partial<QuestionSkillFlags> = {};
      if (
        normalizedGameSkills.skill_listening_speaking !==
        initialGameSkillsRef.current.skill_listening_speaking
      ) {
        globalChangedSkillPayload.skill_listening_speaking =
          normalizedGameSkills.skill_listening_speaking;
      }
      if (
        normalizedGameSkills.skill_lang_nature !==
        initialGameSkillsRef.current.skill_lang_nature
      ) {
        globalChangedSkillPayload.skill_lang_nature =
          normalizedGameSkills.skill_lang_nature;
      }
      if (
        normalizedGameSkills.skill_reading !==
        initialGameSkillsRef.current.skill_reading
      ) {
        globalChangedSkillPayload.skill_reading =
          normalizedGameSkills.skill_reading;
      }
      if (
        normalizedGameSkills.skill_writing !==
        initialGameSkillsRef.current.skill_writing
      ) {
        globalChangedSkillPayload.skill_writing =
          normalizedGameSkills.skill_writing;
      }

      for (
        let questionIndex = 0;
        questionIndex < questionsWithResolvedUrls.length;
        questionIndex += 1
      ) {
        const question = questionsWithResolvedUrls[questionIndex];
        const isExistingQuestion = initialQuestionIdsRef.current.has(
          question.id,
        );
        if (!isExistingQuestion) {
          continue;
        }
        const normalizedHintText = question.hasHint
          ? question.hintText.trim()
          : "";

        const updateQuestionResponse = await multipleChoiceAPI.updateQuestion({
          questionId: question.id,
          no: questionIndex + 1,
          expected_time: expectedTimeValue,
          question: question.text,
          shuffle_answer: normalizeSkillBoolean(question.shuffle_answer),
          hint: normalizedHintText || null,
          image_question: question.imageUrl || null,
          sound_question: question.audioUrl || null,
          image_hint: question.hasHint ? question.hintImageUrl || null : null,
          sound_hint: question.hasHint ? question.hintAudioUrl || null : null,
          ...globalChangedSkillPayload,
          ...changedAudioPayload,
        });

        if (updateQuestionResponse?.status === "failed") {
          setStatusPopUp({
            title: tFB("error"),
            message: `${tFB("updateQuestionError")} ${questionIndex + 1} ${tFB("error")}`,
            type: "error",
          });
          return;
        }

        const initialChoiceIds =
          initialChoiceIdsByQuestionRef.current.get(question.id) ??
          new Set<number>();
        const existingChoicesPayload = question.answers
          .filter((answer) => initialChoiceIds.has(answer.id))
          .map((answer) => ({
            id: answer.id,
            choice: answer.text,
            is_correct: answer.isCorrect,
            image_choice: answer.imageUrl || null,
            sound_choice: answer.audioUrl || null,
          }));

        if (existingChoicesPayload.length > 0) {
          const updateChoiceResponse = await multipleChoiceAPI.updateChoice({
            questionId: question.id,
            choices: existingChoicesPayload,
          });

          if (updateChoiceResponse?.status === "failed") {
            setStatusPopUp({
              title: tFB("error"),
              message: `${tFB("updateOptionError")} ${questionIndex + 1} ${tFB("error")}`,
              type: "error",
            });
            return;
          }
        }

        const newChoiceAnswers = question.answers.filter(
          (answer) => !initialChoiceIds.has(answer.id),
        );

        const newChoicesPayload = newChoiceAnswers.map((answer) => ({
          choice: answer.text,
          is_correct: answer.isCorrect,
          image_choice: answer.imageUrl || null,
          sound_choice: answer.audioUrl || null,
        }));

        if (newChoicesPayload.length > 0) {
          const createChoiceResponse = await multipleChoiceAPI.createChoice({
            questionId: question.id,
            choices: newChoicesPayload,
          });

          if (
            createChoiceResponse &&
            typeof createChoiceResponse === "object" &&
            "status" in createChoiceResponse &&
            createChoiceResponse.status === "failed"
          ) {
            setStatusPopUp({
              title: tFB("error"),
              message: `${tFB("addOptionError")} ${questionIndex + 1} ${tFB("error")}`,
              type: "error",
            });
            return;
          }

          const createdChoices = Array.isArray(
            (createChoiceResponse as { choices?: Array<{ id?: number }> })
              .choices,
          )
            ? (createChoiceResponse as { choices: Array<{ id?: number }> })
                .choices
            : [];

          if (createdChoices.length !== newChoiceAnswers.length) {
            setStatusPopUp({
              title: tFB("error"),
              message: `${tFB("invalidNewOptionData")} ${questionIndex + 1} ${tFB("invalidData")}`,
              type: "error",
            });
            return;
          }

          const createdIdByTempId = new Map<number, number>();
          for (let index = 0; index < newChoiceAnswers.length; index += 1) {
            const createdId = Number(createdChoices[index]?.id);
            if (!Number.isInteger(createdId) || createdId <= 0) {
              setStatusPopUp({
                title: tFB("error"),
                message: `${tFB("newOptionIdNotFound")} ${questionIndex + 1}`,
                type: "error",
              });
              return;
            }

            createdIdByTempId.set(newChoiceAnswers[index].id, createdId);
          }

          questionsWithSyncedChoiceIds = questionsWithSyncedChoiceIds.map(
            (questionItem) => {
              if (questionItem.id !== question.id) {
                return questionItem;
              }

              return {
                ...questionItem,
                answers: questionItem.answers.map((answer) => {
                  const syncedId = createdIdByTempId.get(answer.id);
                  return syncedId ? { ...answer, id: syncedId } : answer;
                }),
              };
            },
          );
        }
      }

      const newQuestionsPayload = questionsWithSyncedChoiceIds
        .map((question, questionIndex) => ({ question, questionIndex }))
        .filter(
          ({ question }) => !initialQuestionIdsRef.current.has(question.id),
        )
        .map(({ question, questionIndex }) => {
          const normalizedHintText = question.hasHint
            ? question.hintText.trim()
            : "";
          const normalizedSkills = extractQuestionSkills(question);
          return {
            no: questionIndex + 1,
            expected_time: expectedTimeValue,
            question: question.text,
            shuffle_answer: normalizeSkillBoolean(question.shuffle_answer),
            hint: normalizedHintText || null,
            image_question: question.imageUrl || null,
            sound_question: question.audioUrl || null,
            image_hint: question.hasHint ? question.hintImageUrl || null : null,
            sound_hint: question.hasHint ? question.hintAudioUrl || null : null,
            skill_listening_speaking: normalizedSkills.skill_listening_speaking,
            skill_lang_nature: normalizedSkills.skill_lang_nature,
            skill_reading: normalizedSkills.skill_reading,
            skill_writing: normalizedSkills.skill_writing,
            audio_question: currentGameAudios.audio_question,
            audio_choice: currentGameAudios.audio_choice,
            audio_assistant_voice: currentGameAudios.audio_assistant_voice,
            audio_background: currentGameAudios.audio_background,
            choices: question.answers.map((answer) => ({
              choice: answer.text,
              is_correct: answer.isCorrect,
              image_choice: answer.imageUrl || null,
              sound_choice: answer.audioUrl || null,
            })),
          };
        });

      if (newQuestionsPayload.length > 0) {
        const createNewQuestionsResponse =
          await multipleChoiceAPI.createMultipleChoice({
            gameId: createdGameData.id,
            questions: newQuestionsPayload,
          });

        if (createNewQuestionsResponse?.status === "failed") {
          setStatusPopUp({
            title: tFB("error"),
            message: tFB("addQuestionError"),
            type: "error",
          });
          return;
        }

        if (previewUuid) {
          const latestGameDetail =
            await mainAPI.getGameinfoAndQuestionByUuid(previewUuid);
          if (
            latestGameDetail &&
            (latestGameDetail as { status?: string }).status !== "failed"
          ) {
            applyLoadedGameDetail(
              latestGameDetail as FlappyBirdProps["initialGameDetail"],
            );
            initialGameAudiosRef.current = currentGameAudios;
            setStatusPopUp(buildGameSuccessStatus(tFB("flappyBird"), "update"));
            setIsUpdateMode(true);
            return;
          }
        }
      }

      setQuestions(questionsWithSyncedChoiceIds);

      initialQuestionIdsRef.current = new Set(
        questionsWithSyncedChoiceIds.map((question) => question.id),
      );
      initialChoiceIdsByQuestionRef.current = new Map(
        questionsWithSyncedChoiceIds.map((question) => [
          question.id,
          new Set(question.answers.map((answer) => answer.id)),
        ]),
      );
      initialQuestionSkillsRef.current = new Map(
        questionsWithSyncedChoiceIds.map((question) => [
          question.id,
          extractQuestionSkills(question),
        ]),
      );
      initialGameAudiosRef.current = currentGameAudios;
      commitSavedQuizQuestionsGameSnapshot(
        savedFormSnapshotRef,
        exerciseName,
        suggestion,
        description,
        { ...gameSkills },
        currentGameAudios,
        questionsWithSyncedChoiceIds,
      );

      setStatusPopUp(buildGameSuccessStatus(tFB("flappyBird"), "update"));
      setIsUpdateMode(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseStatusPopUp = () => {
    closeStatusPopUp(setStatusPopUp, () => {
      void onCompleted;
    });
  };

  const handleStatusButtonClick = () => {
    if (isStatusConfirming || isSubmitting) {
      return;
    }

    if (!createdGameData?.id) {
      setStatusPopUp({
        title: tFB("updateStatusError"),
        message: tFB("noGameDataToChangeStatus"),
        type: "error",
      });
      return;
    }

    setIsStatusConfirmOpen(true);
  };

  const closeStatusConfirm = () => {
    if (isStatusConfirming) {
      return;
    }
    setIsStatusConfirmOpen(false);
  };

  const confirmStatusToggle = async () => {
    if (!createdGameData?.id) {
      setStatusPopUp({
        title: tFB("updateStatusError"),
        message: tFB("noGameDataToChangeStatus"),
        type: "error",
      });
      setIsStatusConfirmOpen(false);
      return;
    }

    const nextStatus = !isPublished;
    const dialogContent = getStatusToggleDialogContent(
      isPublished,
      statusToggleDialogLabels,
    );

    setIsStatusConfirming(true);
    const response = await mainAPI.updateGameStatus({
      id: createdGameData.id,
      status: nextStatus,
    });
    setIsStatusConfirming(false);
    setIsStatusConfirmOpen(false);

    if (!response || response.status === "failed") {
      setStatusPopUp({
        title: tFB("updateStatusError"),
        message: tFB("changeStatusError"),
        type: "error",
      });
      return;
    }

    setIsPublished(nextStatus);
    setStatusPopUp({
      title: dialogContent.successTitle,
      message: dialogContent.successMessage,
      type: "success",
    });
  };

  const isExpectedTimeValid = useMemo(() => {
    const trimmed = expectedTime.trim();
    if (trimmed === "") {
      return false;
    }
    const parsed = Number(trimmed);
    return Number.isInteger(parsed) && parsed >= 0;
  }, [expectedTime]);
  const expectedTimeDisplay = useMemo(() => {
    if (!isExpectedTimeValid) {
      return "";
    }
    const value = Number(expectedTime.trim());
    if (value > 59) {
      const minutes = value / 60;
      const minuteText = Number.isInteger(minutes)
        ? String(minutes)
        : minutes.toFixed(2).replace(/\.?0+$/, "");
      return `${minuteText} ${tMain("expectedTimeMinuteUnit")}`;
    }
    return `${value} ${tMain("expectedTimeSecondUnit")}`;
  }, [expectedTime, isExpectedTimeValid, tMain]);
  const isSaveReady = useMemo(
    () => areQuizQuestionsSaveReady(questions) && isExpectedTimeValid,
    [questions, isExpectedTimeValid],
  );

  const currentFormSnapshot = useMemo(
    () =>
      createQuizQuestionsGameSnapshot(
        exerciseName,
        suggestion,
        description,
        gameSkills,
        gameAudios,
        questions,
      ),
    [exerciseName, suggestion, description, gameSkills, gameAudios, questions],
  );
  const hasUnsavedChanges = useHasUnsavedGameFormChanges(
    isUpdateMode,
    currentFormSnapshot,
    savedFormSnapshotRef,
  );
  useReportGameEditorLeaveGuard(
    getShouldConfirmLeaveGameEditor(isUpdateMode, hasUnsavedChanges),
  );
  const canPreview = getCanGamePreview(
    isUpdateMode,
    previewUuid,
    hasUnsavedChanges,
  );

  const hasMissingCorrectChoice = () => {
    const missingCorrectChoice =
      findQuizQuestionMissingCorrectChoice(questions);
    if (!missingCorrectChoice) {
      return false;
    }

    const { questionIndex, question } = missingCorrectChoice;
    setSelectedQuestionId(question.id);
    openStatusPopUp(
      setStatusPopUp,
      createGameFieldErrorPopUp(
        tFB("error"),
        `${tFB("questionNumber")} ${questionIndex + 1} ${tFB("mustSelectCorrectAnswer")}`,
        quizQuestionFieldTarget(question.id, "answers"),
      ),
    );
    return true;
  };

  const handleClickFinishButton = () => {
    if (isSubmitting || !isSaveReady) {
      return;
    }

    if (hasMissingCorrectChoice()) {
      return;
    }

    setIsConfirmSaveOpen(true);
  };

  const handleCancelConfirmSave = () => {
    setIsConfirmSaveOpen(false);
  };

  const handleConfirmSave = () => {
    setIsConfirmSaveOpen(false);
    if (isUpdateMode) {
      void handleUpdateFlappyBird();
      return;
    }

    void handleSubmitFlappyBird();
  };

  const handleOpenPreview = () => {
    if (!canPreview || hasUnsavedChanges || !isExpectedTimeValid) {
      if (!isExpectedTimeValid) {
        setStatusPopUp({
          title: tFB("error"),
          message: tMain("expectedTimeInvalid"),
          type: "error",
        });
      } else if (hasUnsavedChanges) {
        setStatusPopUp({
          title: PREVIEW_UNSAVED_TITLE,
          message: PREVIEW_UNSAVED_MESSAGE,
          type: "info",
        });
      }
      return;
    }

    if (!previewUuid) {
      setStatusPopUp({
        title: tFB("previewError"),
        message: tFB("noGameUUIDForPreview"),
        type: "error",
      });
      return;
    }

    const previewBaseUrl =
      process.env.NEXT_PUBLIC_BASE_GAME_URL ||
      "https://thaipilagame.gforcesolution.com";
    const normalizedBaseUrl = previewBaseUrl.replace(/\/$/, "");
    const previewUrl = `${normalizedBaseUrl}/${encodeURIComponent(previewUuid)}?preview`;
    setPreviewIframeUrl(previewUrl);
  };

  const handleClosePreview = () => {
    setPreviewIframeUrl(null);
  };

  return (
    <section className="mx-auto w-full rounded px-4 py-3 text-[#3f3f3f] lg:px-10">
      <div className="border-b border-[#dddddd] pb-2 text-2xl leading-none">
        <span className="font-semibold text-[#4E4E4E]">
          {tFB("editContent")} &gt;{" "}
        </span>
        <span className="font-bold text-[#2b2b2b]">{tFB("flappyBird")}</span>
      </div>

      <div className="mt-4 space-y-3">
        <div>
          <label className="mb-1 block text-lg font-medium text-[#5b5b5b]">
            {tFB("activityName")}
          </label>
          <input
            value={exerciseName}
            onChange={(event) => setExerciseName(event.target.value)}
            className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
          />
        </div>

        <div>
          <label className="mb-1 flex items-center gap-1 text-lg font-medium text-[#5b5b5b]">
            {tFB("details")}
          </label>
          <input
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
            placeholder={tFB("selectCorrectAnswer")}
          />
        </div>

        {/* <div>
                    <label className="mb-1 flex items-center gap-1 text-lg font-medium text-[#5b5b5b]">
                        {tFB("questionTitle")}
                    </label>
                    <input
                        value={suggestion}
                        onChange={(event) => setSuggestion(event.target.value)}
                        className="h-10 w-full rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
                        // placeholder={tFB("selectCorrectAnswer")}
                    />
                </div> */}

        <div className="flex items-center gap-3">
          <span className="text-lg font-medium text-[#5b5b5b]">
            {tFB("status")}:
          </span>
          <button
            type="button"
            onClick={handleStatusButtonClick}
            disabled={
              !createdGameData?.id || isSubmitting || isStatusConfirming
            }
            className={getPublishButtonClass(isPublished)}
          >
            {renderPublishStatusContent(isPublished, publishStatusLabels, 16)}
          </button>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        <div className="overflow-x-auto pb-1">
          <div className="grid grid-cols-[40px_1fr_1fr_60px] min-w-[680px] items-center gap-4">
            <span className="text-lg font-medium text-[#5a5a5a] text-center">
              {tFB("order")}
            </span>
          </div>
        </div>

        {questions.map((question, index) => (
          <QuestionBlock
            key={question.id}
            index={index + 1}
            question={question}
            onQuestionTextChange={updateQuestionText}
            onQuestionHintTextChange={updateQuestionHintText}
            onQuestionHintImageFileChange={updateQuestionHintImageFile}
            onQuestionHintAudioFileChange={updateQuestionHintAudioFile}
            onEnableQuestionHint={enableQuestionHint}
            onClearQuestionHint={(questionId) =>
              requestDeleteQuestionMediaField(questionId, "hint")
            }
            onQuestionImageFileChange={updateQuestionImageFile}
            onQuestionAudioFileChange={updateQuestionAudioFile}
            onQuestionImageDelete={(questionId) =>
              requestDeleteQuestionMediaField(questionId, "image_question")
            }
            onQuestionAudioDelete={(questionId) =>
              requestDeleteQuestionMediaField(questionId, "sound_question")
            }
            onQuestionHintImageDelete={(questionId) =>
              requestDeleteQuestionMediaField(questionId, "image_hint")
            }
            onQuestionHintAudioDelete={(questionId) =>
              requestDeleteQuestionMediaField(questionId, "sound_hint")
            }
            onAnswerTextChange={updateAnswerText}
            onQuestionShuffleAnswerChange={updateQuestionShuffleAnswer}
            onAnswerImageFileChange={updateAnswerImageFile}
            onAnswerAudioFileChange={updateAnswerAudioFile}
            onAnswerImageDelete={(questionId, answerId) =>
              requestDeleteChoiceMediaField(
                questionId,
                answerId,
                "image_choice",
              )
            }
            onAnswerAudioDelete={(questionId, answerId) =>
              requestDeleteChoiceMediaField(
                questionId,
                answerId,
                "sound_choice",
              )
            }
            onToggleAnswerCorrect={toggleAnswerCorrect}
            onAddAnswer={addAnswer}
            onRemoveAnswer={removeAnswer}
            onRemoveQuestion={removeQuestion}
            onMoveQuestionUp={moveQuestionUp}
            onMoveQuestionDown={moveQuestionDown}
            onSelectQuestion={setSelectedQuestionId}
            isSelected={question.id === selectedQuestionId}
            canMoveUp={index > 0}
            canMoveDown={index < questions.length - 1}
            canRemoveQuestion={canRemoveQuestion}
            fieldErrorHighlightKey={fieldErrorHighlightKey}
          />
        ))}
      </div>

      <div className="mt-4">
        <button
          type="button"
          onClick={addQuestion}
          className="ml-0 md:ml-8 lg:ml-12 cursor-pointer rounded border border-[#adadad] bg-[#e7e7e7] px-3 py-1.5 text-lg font-semibold text-[#535353] hover:bg-[#d6d6d6]"
        >
          {tFB("addQuestion")}
        </button>
      </div>

      <div className="mt-5 w-full p-3">
        <label className="mb-1 block text-lg font-medium text-[#5b5b5b]">
          {tMain("expectedTimeLabel")}
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <input
            value={expectedTime}
            onChange={(event) => {
              const nextValue = event.target.value;
              if (/^\d*$/.test(nextValue)) {
                setExpectedTime(nextValue);
              }
            }}
            required
            inputMode="numeric"
            className="h-10 w-full max-w-[360px] rounded border border-[#bfc2c8] bg-white px-3 text-lg outline-none"
            placeholder={tMain("expectedTimePlaceholder")}
          />
          {expectedTimeDisplay ? (
            <span className="text-base font-semibold text-[#4a4a4a]">
              {expectedTimeDisplay}
            </span>
          ) : null}
        </div>
      </div>

      <div className="w-full p-3">
        <div className="grid w-full grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start">
          <div className="rounded-lg border border-[#e0e0e0] bg-[#f9f9f9] p-4">
            <div className="flex items-start gap-3 pb-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#f0f0f0]">
                <TbCategory2 className="text-xl text-[#555]" />
              </div>
              <div className="min-w-0">
                <div className="text-base font-bold text-[#333]">
                  {tFB("competency")}
                </div>
                <div className="text-sm text-[#888]">
                  {tFB("competencySubtitle")}
                </div>
              </div>
            </div>
            <hr className="mb-4 border-t border-[#e8e8e8]" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-stretch">
              <label className="flex h-full cursor-pointer items-center gap-2 rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5 hover:bg-[#fafafa]">
                <GiBrain className="shrink-0 text-xl text-[#555]" />
                <span className="flex-1 text-sm text-[#444]">
                  {tFB("listeningSpeakingThinking")}
                </span>
                <input
                  type="checkbox"
                  checked={gameSkills.skill_listening_speaking}
                  onChange={(event) =>
                    updateGameSkill(
                      "skill_listening_speaking",
                      event.target.checked,
                    )
                  }
                  className="h-4 w-4 shrink-0 rounded border-[#ccc]"
                />
              </label>
              <label className="flex h-full cursor-pointer items-center gap-2 rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5 hover:bg-[#fafafa]">
                <HiOutlineBookOpen className="shrink-0 text-xl text-[#555]" />
                <span className="flex-1 text-sm text-[#444]">
                  {tFB("readingForThinking")}
                </span>
                <input
                  type="checkbox"
                  checked={gameSkills.skill_reading}
                  onChange={(event) =>
                    updateGameSkill("skill_reading", event.target.checked)
                  }
                  className="h-4 w-4 shrink-0 rounded border-[#ccc]"
                />
              </label>
              <label className="flex h-full cursor-pointer items-center gap-2 rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5 hover:bg-[#fafafa]">
                <MdOutlineChat className="shrink-0 text-xl text-[#555]" />
                <span className="flex-1 text-sm text-[#444]">
                  {tFB("understandingThaiLanguage")}
                </span>
                <input
                  type="checkbox"
                  checked={gameSkills.skill_lang_nature}
                  onChange={(event) =>
                    updateGameSkill("skill_lang_nature", event.target.checked)
                  }
                  className="h-4 w-4 shrink-0 rounded border-[#ccc]"
                />
              </label>
              <label className="flex h-full cursor-pointer items-center gap-2 rounded-lg border border-[#e0e0e0] bg-white px-3 py-2.5 hover:bg-[#fafafa]">
                <HiOutlinePencilAlt className="shrink-0 text-xl text-[#555]" />
                <span className="flex-1 text-sm text-[#444]">
                  {tFB("writingForThinking")}
                </span>
                <input
                  type="checkbox"
                  checked={gameSkills.skill_writing}
                  onChange={(event) =>
                    updateGameSkill("skill_writing", event.target.checked)
                  }
                  className="h-4 w-4 shrink-0 rounded border-[#ccc]"
                />
              </label>
            </div>
          </div>

          <div className="rounded-lg border border-[#e0e0e0] bg-[#f9f9f9] p-4">
            <div className="flex items-start gap-3 pb-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#f0f0f0]">
                <HiOutlineVolumeUp className="text-xl text-[#555]" />
              </div>
              <div className="min-w-0">
                <div className="text-base font-bold text-[#333]">
                  {tFB("sound")}
                </div>
                <div className="text-sm text-[#888]">
                  {tFB("soundSubtitle")}
                </div>
              </div>
            </div>
            <hr className="mb-4 border-t border-[#e8e8e8]" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="overflow-hidden rounded-lg border border-[#e0e0e0]">
                <div className="flex items-center gap-2 bg-[#f0f4f7] px-3 py-2.5">
                  <HiOutlineMicrophone className="text-lg text-[#555]" />
                  <span className="text-sm font-semibold text-[#444]">
                    {tFB("narration")}
                  </span>
                </div>
                <div className="space-y-3 bg-white p-3">
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#444]">
                    <input
                      type="checkbox"
                      checked={gameAudios.audio_question}
                      onChange={(event) =>
                        updateGameAudio("audio_question", event.target.checked)
                      }
                      className="h-4 w-4 rounded border-[#ccc]"
                    />
                    {tFB("muteNarration")}
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#444]">
                    <input
                      type="checkbox"
                      checked={gameAudios.audio_choice}
                      onChange={(event) =>
                        updateGameAudio("audio_choice", event.target.checked)
                      }
                      className="h-4 w-4 rounded border-[#ccc]"
                    />
                    {tFB("muteOptionNarration")}
                  </label>
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-[#e0e0e0]">
                <div className="flex items-center gap-2 bg-[#f0f4f7] px-3 py-2.5">
                  <IoGameControllerOutline className="text-lg text-[#555]" />
                  <span className="text-sm font-semibold text-[#444]">
                    {tFB("gameSound")}
                  </span>
                </div>
                <div className="space-y-3 bg-white p-3">
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#444]">
                    <input
                      type="checkbox"
                      checked={gameAudios.audio_assistant_voice}
                      onChange={(event) =>
                        updateGameAudio(
                          "audio_assistant_voice",
                          event.target.checked,
                        )
                      }
                      className="h-4 w-4 rounded border-[#ccc]"
                    />
                    {tFB("muteAssistant")}
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-[#444]">
                    <input
                      type="checkbox"
                      checked={gameAudios.audio_background}
                      onChange={(event) =>
                        updateGameAudio(
                          "audio_background",
                          event.target.checked,
                        )
                      }
                      className="h-4 w-4 rounded border-[#ccc]"
                    />
                    {tFB("muteBackground")}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
          {isUpdateMode && previewUuid ? (
            <button
              type="button"
              onClick={handleOpenPreview}
              disabled={
                hasUnsavedChanges || isSubmitting || !isExpectedTimeValid
              }
              className={GAME_PREVIEW_BUTTON_CLASS}
              title={
                hasUnsavedChanges
                  ? PREVIEW_BUTTON_DISABLED_TITLE
                  : tFB("preview")
              }
            >
              {tFB("preview")}
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleClickFinishButton}
            disabled={isSubmitting || !isSaveReady}
            className="cursor-pointer rounded bg-[#f4d735] px-7 py-2 text-lg font-semibold text-[#4a4a4a] shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:bg-[#e0c91a] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSubmitting ? tFB("saving") : tFB("save")}
          </button>
        </div>
      </div>

      {isConfirmSaveOpen ? (
        <ConfirmPopUp
          title={tFB("confirmSave")}
          message={tFB("savePrompt")}
          cancelText={tFB("cancel")}
          confirmText={tFB("confirm")}
          onCancel={handleCancelConfirmSave}
          onConfirm={handleConfirmSave}
          isConfirming={isSubmitting}
        />
      ) : null}

      {isStatusConfirmOpen ? (
        <ConfirmPopUp
          title={
            getStatusToggleDialogContent(isPublished, statusToggleDialogLabels)
              .confirmTitle
          }
          message={
            getStatusToggleDialogContent(isPublished, statusToggleDialogLabels)
              .confirmMessage
          }
          confirmText={tFB("confirm")}
          cancelText={tFB("cancel")}
          onConfirm={() => {
            void confirmStatusToggle();
          }}
          onCancel={closeStatusConfirm}
          isConfirming={isStatusConfirming}
        />
      ) : null}

      {pendingMediaDelete !== null ? (
        <ConfirmPopUp
          title={tFB("confirmDelete")}
          message={`${tFB("wantToDelete")} ${getDeleteFieldLabel(pendingMediaDelete.field)} ${tFB("or")}`}
          cancelText={tFB("cancel")}
          confirmText={tFB("confirm")}
          onCancel={() => {
            if (isDeletingMedia) {
              return;
            }
            setPendingMediaDelete(null);
          }}
          onConfirm={() => {
            void confirmDeleteMediaField();
          }}
          isConfirming={isDeletingMedia}
        />
      ) : null}

      {statusPopUp ? (
        <StatusPopUp
          title={statusPopUp.title}
          message={statusPopUp.message}
          type={statusPopUp.type}
          onClose={handleCloseStatusPopUp}
        />
      ) : null}

      {previewIframeUrl ? (
        <GamePreviewModal
          src={previewIframeUrl}
          iframeTitle={`${tFB("flappyBird")} ${tFB("preview")}`}
          onClose={handleClosePreview}
          closeVariant="danger"
          shellVariant="editor"
        />
      ) : null}
    </section>
  );
}
